import { Component, HostBinding, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertController, IonicModule, ToastController } from '@ionic/angular';
import { NavNyomtatvanyService, NyCsomopont, NyElrendezes, NyHiba, NyMezo, NyMezoElrendezes, NyModell } from '../nav-nyomtatvany.service';
import { AnykHely, AnykLapComponent } from '../anyk-lap/anyk-lap.component';
import { AnykSzabalymotor } from '../anyk-szabalymotor';
import { AnykKotes } from '../anyk-kotes';
import { kepAdatUri, keretbeNyomtat, lapArany } from '../nyomtatas-keret';
import { KeretService } from '../../keret.service';
import { modeFavicon } from '../../mode-nevek';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { Subscription } from 'rxjs';
import { SzerkZar, SzerkZarService } from '../../components/szerk-zar/szerk-zar.service';
import { SzerkZarSavComponent } from '../../components/szerk-zar/szerk-zar-sav.component';

interface Tabla {
  oszlopok: { betu: string; cim: string }[]; sorok: { sor: string; cim: string; cellak: Record<string, NyMezo> }[]; egyeb: (NyMezo | NyCsomopont)[];
  /** Az egyszerű mezők rácsba szedve (egymás után következő mezők egy rácsban), köztük a belső csomópontok. */
  szakaszok: ({ racs: NyMezo[] } | { csomopont: NyCsomopont })[];
}

/**
 *  NAV-NYOMTATVÁNY KITÖLTŐ – az ÁNYK mintájára, hogy a könyvelő ismerős helyen találja a dolgokat:
 *   - bal oldalt a LAPOK: részbizonylatonként a lapok (blokkok); az ismétlődő részbizonylatnak
 *     (pl. 2608M) példányai vannak, ezek vehetők fel / törölhetők;
 *   - középen EGY LAP: a mezőcsoportok keretben, a sorszámos mezők ÁNYK-táblázatként
 *     (sorszám · megnevezés · a, b, c … oszlop), az ismétlődő sorok (lánc) bővíthető táblában;
 *   - alul a HIBALISTA (kattintásra a hibás mezőre ugrik) és a mező-információs sor
 *     (mezőkód, rövid kód, szabály).
 *  A mezők az XSD-ből kapott szabályokkal azonnal ellenőrződnek; mentéskor a kiszolgáló is.
 *  Gyorsbillentyűk: Ctrl+S mentés, F6 ellenőrzés, Ctrl+P nyomtatás, Ctrl+PgDn/PgUp következő/előző lap.
 */
@Component({
  selector: 'app-nav-kitolto',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, AnykLapComponent, DragDropModule, SzerkZarSavComponent],
  templateUrl: './kitolto.component.html',
  styleUrls: ['./kitolto.component.scss'],
})
export class NavKitoltoComponent implements OnInit, OnDestroy {
  @HostBinding('class.ber') get berMod() { return this.modul === 'BER'; }
  @HostBinding('class.gepjarmu') get gepjarmuMod() { return this.modul === 'GEPJARMU'; }
  /** Az útvonal modulja (NAPLO / BÉR / GÉPJÁRMŰ – a cégautóadó 2601-e, 2026-10-18 óta). */
  modul: 'NAPLO' | 'BER' | 'GEPJARMU' = 'NAPLO';
  bev: any = null;
  modell: NyModell | null = null;
  adat: any = {};
  hibak: NyHiba[] = [];
  mezoHiba: Record<string, string> = {};
  aktForm = '';
  aktPeldany = 0;
  aktBlokk = 0;
  fokusz: NyMezo | null = null;
  piszkos = false;
  /** Mit tud a modul előtölteni (pl. „bérszámfejtés (havi összesítő)”), vagy null. */
  kitoltheto: string | null = null;
  /** Az előtöltés csak kiegészít (törzsadat), nem épít újra – a gomb és az üzenet is más. */
  kitoltesKiegeszit = false;
  /** Gépi beküldés: csak XSD-alapú sablonnál; a környezetet az ügyfél NAV-kapcsolata dönti el. */
  bekuldheto = false;
  /*  XSD nélküli (ONYA- vagy ÁNYK-forrású) nyomtatvány: XML nem készül belőle, a kiszolgáló
   *  megmondja, hol adható be – ONYA (pl. 2658, gépeléssel, a NAV előtölti) vagy eÁFA (65). */
  beadas: { ut: 'onya' | 'eafa'; uzenet: string } | null = null;
  /*  Gépi beküldhetőség a NAV sablontára szerint: `van === false` → a NAV ezt a nyomtatványt még
   *  nem fogadja (nincs hozzá XPath-állomány). Ilyenkor előre szólunk, és a gombok nem hívják a NAV-ot. */
  gepi: any = null;
  get gepiNemMegy(): boolean { return this.bekuldheto && this.gepi?.van === false; }
  m2mKornyezet: 'teszt' | 'eles' = 'teszt';
  m2mFut: string | null = null;
  /*  TECHNIKAI MEZŐK MUTATÁSA. Ami nincs az XSD-ben, az nem kerül a beküldött adatba, de a
   *  nyomtatványképhez tartozhat (pótlapszám, ügyintéző, lapjelzők). Kitölteni sosem lehet őket;
   *  a megjelenítésük a sablonok ellenőrzéséhez kell, ezért csak fejlesztői rendszeren
   *  kapcsolható (ott alapból be is van kapcsolva). */
  technikaiMutat = false;
  elotoltFut = false;
  get zarolt(): boolean { return ['bekuldve', 'befogadva'].includes(this.bev?.allapot); }
  ment = false;
  hibalistaNyitva = false;
  betoltesHiba = '';
  /* Fejlesztői elrendezés-szerkesztő (csak nem éles rendszeren – a kiszolgáló dönti el). */
  elr: NyElrendezes = { mezok: {}, blokkok: {} };
  szerkesztheto = false;
  szerkMod = false;
  elrPiszkos = false;

  /* ÁNYK-sablon (.enyk): a lapok grafikus képe („Nyomtatványkép” nézet, nyomtatás). */
  enyk: any = null;
  nezet: 'tabla' | 'lapkep' = 'tabla';
  aktLap = 0;          // az aktuális ÁNYK-lap indexe az aktuális form oldalai között
  aktDin = 0;          // dinamikus lap: a láncsor indexe
  nyomtatasFut = false;
  get logo(): string { return modeFavicon(this.modul); }
  /** A bevallás szerkesztési zárja (components/szerk-zar) – csak amíg nincs beküldve. */
  zar: SzerkZar | null = null;
  private zarKor = 0;
  private vege = false;
  private psub?: Subscription;

  constructor(private ny: NavNyomtatvanyService, private route: ActivatedRoute, private router: Router,
              private toast: ToastController, private alert: AlertController, private keret: KeretService,
              private szerkZar: SzerkZarService) {}

  async ngOnInit() {
    const rm = this.route.snapshot.data['modul'];
    this.modul = rm === 'BER' || rm === 'GEPJARMU' ? rm : 'NAPLO';
    //  A másik alrendszer útvonalán nyílt meg (pl. könyvelésből egy /ber/bevallas): az alrendszert is
    //  átváltjuk, különben a menü a régié marad, és a Vissza nem talál haza.
    if (this.keret.modulba(this.modul, this.router.url)) return;
    /*  Ugyanez a képernyő más bevallással (az útvonal paramétere változott, a komponens megmaradt): a régi
     *  bevallás zárját elengedjük, és tisztán újranyitjuk – mint a javító bevallásnál. */
    const kezdoId = this.route.snapshot.paramMap.get('id');
    this.psub = this.route.paramMap.subscribe((p) => {
      if (p.get('id') === kezdoId || this.vege) return;
      this.zarElenged();
      const cel = this.router.url;
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => this.router.navigateByUrl(cel));
    });
    try {
      const r: any = await this.ny.get('/bevallas/' + kezdoId);
      this.bev = r.bevallas; this.modell = r.modell; this.adat = r.bevallas.adat || {};
      this.elr = { mezok: {}, blokkok: {}, ...(r.elrendezes || {}) }; this.elr.mezok = this.elr.mezok || {}; this.elr.blokkok = this.elr.blokkok || {};
      this.szerkesztheto = !!r.szerkesztheto;
      if (this.szerkesztheto) {
        try { this.technikaiMutat = localStorage.getItem('nyomtatvany-technikai') !== '0'; } catch { this.technikaiMutat = true; }
      }
      this.kitoltheto = r.kitoltheto || null;
      this.kitoltesKiegeszit = !!r.kitoltesKiegeszit;
      this.bekuldheto = !!r.bekuldheto;
      this.beadas = r.beadas || null;
      this.gepi = r.gepiBekuldes || null;
      this.m2mBetolt();
      for (const f of this.modell!.formok) if (!Array.isArray(this.adat[f.nev])) this.adat[f.nev] = f.min >= 1 ? [{}] : [];
      this.aktForm = this.modell!.formok[0]?.nev || '';
      this.ellenoriz(false);
      let enykKesz: Promise<void> = Promise.resolve();
      if (r.vanEnyk) {
        // Az ÁNYK-sablonnal a nyomtatványkép az alapnézet (a táblázatos a gombbal választható).
        enykKesz = this.ny.get(`/sablon/${this.modell!.kod}/enyk`).then((e) => { this.enyk = e; this.kotesEpit(); this.motorIndit(); this.nezet = 'lapkep'; }).catch(() => { this.enyk = null; });
      }
      /*  ?nyomtat=1 (a munkatervből „Bevallás PDF”): betöltés után rögtön a teljes bevallás nyomtatása –
       *  a nyomtatási ablakban „Mentés PDF-ként” választható. */
      if (this.route.snapshot.queryParamMap.get('nyomtat') === '1') {
        enykKesz.then(() => setTimeout(() => (this.enyk ? this.nyomtatasIndit('koteg') : window.print()), 600));
      }
      // Ha a modul adataiból még sosem töltöttük elő, és senki nem szerkesztette (a létrehozás óta
      // nem mentették), előtöltjük magától – szerkesztett bevallást csak a gombbal, kérdés után.
      const erintetlen = Math.abs(Date.parse(this.bev.modositva) - Date.parse(this.bev.letrehozva)) < 5000;
      await this.zarFoglal();
      if (this.kitoltheto && !this.bev.kitoltve && !this.zarolt && erintetlen && (!this.zar || this.zar.irhato)) await this.elotolt(false);
    } catch (e: any) { this.betoltesHiba = e?.error?.error || 'Nem sikerült betölteni a bevallást.'; }
  }
  ngOnDestroy() { this.vege = true; this.psub?.unsubscribe(); this.zarElenged(); }

  /*  SZERKESZTÉSI ZÁR: amíg más szerkeszti, itt csak olvasható (mentés, előtöltés, beküldés tiltva).
   *  A zár annak a modulnak a kapuja mögött kérhető, amelyé a bevallás (NAPLO / BÉR). */
  private async zarFoglal() {
    this.zarElenged();
    if (this.vege || !this.bev?.id || this.zarolt) return;
    const id = String(this.bev.id), kor = this.zarKor;
    const bm = String(this.bev.modul || this.modul).toUpperCase();
    const modul = bm === 'BER' ? 'ber' : bm === 'GEPJARMU' ? 'gepjarmu' : 'naplo';
    const z = await this.szerkZar.foglal(modul, 'nav_bevallas', id, { ujratolt: () => this.ujratolt(id) });
    if (kor !== this.zarKor || this.vege || String(this.bev?.id) !== id) { z?.felenged(); return; }
    this.zar = z;
  }
  private zarElenged() { this.zarKor++; this.zar?.felenged(); this.zar = null; }
  get zarTilt(): boolean { return !!this.zar && !this.zar.irhato; }
  /*  Friss adat a kiszolgálóról (a másik szerkesztő befejezte, vagy a sáv „Újratöltés"-e): az előtöltés
   *  utáni állapothoz hasonlóan tiszta lap, mentetlen változás nélkül. */
  private async ujratolt(id: string) {
    const r: any = await this.ny.get('/bevallas/' + id);
    if (this.vege || String(this.bev?.id) !== id) return;
    this.bev = r.bevallas; this.adat = r.bevallas.adat || {};
    for (const f of this.modell!.formok) if (!Array.isArray(this.adat[f.nev])) this.adat[f.nev] = f.min >= 1 ? [{}] : [];
    this.aktPeldany = 0; this.aktDin = 0;
    if (this.enyk) this.kotesEpit();
    this.ellenoriz(false);
    this.piszkos = false;
    if (this.zarolt) this.zarElenged();      // közben beküldték – már nincs mit szerkeszteni
  }

  /* ── navigáció ── */
  get formok(): NyCsomopont[] { return this.modell?.formok || []; }
  form(nev: string): NyCsomopont | undefined { return this.formok.find((f) => f.nev === nev); }
  get aktFormObj(): NyCsomopont | undefined { return this.form(this.aktForm); }
  get blokkok(): NyCsomopont[] { return (this.aktFormObj?.elemek || []) as NyCsomopont[]; }
  get blokk(): NyCsomopont | undefined { return this.blokkok[this.aktBlokk]; }
  get peldany(): any { return this.adat[this.aktForm]?.[this.aktPeldany] || null; }
  ismetlodo(f: NyCsomopont) { return f.max === null || (f.max || 1) > 1; }
  lapra(formNev: string, peldany: number, blokk: number) {
    this.aktForm = formNev; this.aktPeldany = peldany; this.aktBlokk = blokk; this.fokusz = null;
    document.querySelector('.any-lap')?.scrollTo({ top: 0 });
  }
  kovetkezo(irany: 1 | -1) {
    const n = this.aktBlokk + irany;
    if (n >= 0 && n < this.blokkok.length) this.lapra(this.aktForm, this.aktPeldany, n);
  }

  /** Az ismétlődő részbizonylat példányának felirata (pl. M-lap: név, adóazonosító). */
  peldanyCim(f: NyCsomopont, p: any, i: number): string {
    if (!p) return `${i + 1}.`;
    const mezok = this.osszesMezo(f.elemek?.[0] as NyCsomopont);
    const nev = mezok.filter((m) => /(^|_)(vnev|knev|nev)$/.test(m.technev || '')).map((m) => p[m.kod]).filter(Boolean).join(' ');
    const azon = mezok.find((m) => /adoazonosito/.test(m.technev || ''));
    const a = azon ? p[azon.kod] : '';
    return `${i + 1}. ${nev || '(név nélkül)'}${a ? ' · ' + a : ''}`;
  }
  osszesMezo(n: NyCsomopont | undefined): NyMezo[] {
    if (!n) return [];
    const out: NyMezo[] = [];
    const b = (x: any) => { if (x.tipus === 'mezo') out.push(x); else if (x.tipus !== 'lanc') (x.elemek || []).forEach(b); };
    b(n);
    return out;
  }
  ujPeldany(f: NyCsomopont) {
    this.adat[f.nev] = this.adat[f.nev] || [];
    if (f.max !== null && this.adat[f.nev].length >= (f.max || 1)) return;
    this.adat[f.nev].push({});
    this.piszkos = true;
    this.lapra(f.nev, this.adat[f.nev].length - 1, 0);
  }
  async peldanyTorol(f: NyCsomopont, i: number) {
    const a = await this.alert.create({ header: 'Lap törlése', message: `Törlöd a(z) ${this.peldanyCim(f, this.adat[f.nev][i], i)} lapot és minden adatát?`,
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Törlés', role: 'destructive' }] });
    await a.present();
    if ((await a.onDidDismiss()).role !== 'destructive') return;
    this.adat[f.nev].splice(i, 1);
    this.piszkos = true;
    this.lapra(f.nev, Math.max(0, Math.min(i, this.adat[f.nev].length - 1)), 0);
    this.ellenoriz(false);
  }
  blokkHibaDb(formNev: string, peldany: number, b: NyCsomopont): number {
    const lap = b.lap || b.cim;
    return this.hibak.filter((h) => h.form === formNev && h.peldany === peldany && h.lap === lap).length;
  }

  /* ── ÁNYK-táblázat: a sorszámos mezők sor × oszlop szerint ── */
  private tablaTar = new Map<string, Tabla>();
  tabla(cs: NyCsomopont): Tabla {
    const van = this.tablaTar.get(cs.nev);
    if (van) return van;
    const oszlopok = new Map<string, string>();
    const sorok = new Map<string, { sor: string; cim: string; cellak: Record<string, NyMezo> }>();
    const egyeb: (NyMezo | NyCsomopont)[] = [];
    for (const e of cs.elemek) {
      const m = e as NyMezo;
      if (m.tipus === 'mezo' && m.sor && m.oszlop) {
        if (!oszlopok.has(m.oszlop)) oszlopok.set(m.oszlop, m.oszlopCim || m.oszlop);
        if (!sorok.has(m.sor)) sorok.set(m.sor, { sor: m.sor, cim: m.sorCim || '', cellak: {} });
        sorok.get(m.sor)!.cellak[m.oszlop] = m;
      } else egyeb.push(e);
    }
    // Az azonos előtagú egyszerű mezők („Székhelye ¦ …”) elé egyszer alcsoport-fej kerül.
    let elozo: string | undefined;
    for (const e of egyeb) {
      const m = e as NyMezo;
      if (m.tipus !== 'mezo') { elozo = undefined; continue; }
      m._alfej = m.elotag && m.elotag !== elozo ? m.elotag : undefined;
      elozo = m.elotag;
    }
    // A fejlesztői szerkesztőben beállított sorrend (ismeretlen/új mezők a végére, eredeti sorrendben).
    const rend = this.elr.sorrend?.[cs.nev];
    if (rend?.length) {
      const hely = new Map(rend.map((k, i) => [k, i]));
      const mezok = egyeb.filter((e) => (e as NyMezo).tipus === 'mezo');
      const rendezett = [...mezok].sort((a, b) => (hely.get((a as NyMezo).kod) ?? 1e6) - (hely.get((b as NyMezo).kod) ?? 1e6));
      let j = 0;
      for (let i = 0; i < egyeb.length; i++) if ((egyeb[i] as NyMezo).tipus === 'mezo') egyeb[i] = rendezett[j++];
      let elozo2: string | undefined;
      for (const e of egyeb) { const m = e as NyMezo; if (m.tipus !== 'mezo') { elozo2 = undefined; continue; } m._alfej = m.elotag && m.elotag !== elozo2 ? m.elotag : undefined; elozo2 = m.elotag; }
    }
    const szakaszok: Tabla['szakaszok'] = [];
    for (const e of egyeb) {
      if ((e as NyMezo).tipus === 'mezo') {
        const utolso = szakaszok[szakaszok.length - 1] as { racs?: NyMezo[] } | undefined;
        if (utolso?.racs) utolso.racs.push(e as NyMezo); else szakaszok.push({ racs: [e as NyMezo] });
      } else szakaszok.push({ csomopont: e as NyCsomopont });
    }
    const t: Tabla = {
      oszlopok: [...oszlopok.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([betu, cim]) => ({ betu, cim })),
      sorok: [...sorok.values()],
      egyeb, szakaszok,
    };
    this.tablaTar.set(cs.nev, t);
    return t;
  }

  /* ── értékek, azonnali ellenőrzés ──
   *  Minden mező egy adatobjektumban él: a lap példányában (ut = ''), egy láncsorban
   *  (ut = 'Chain_X[0]', beágyazva 'Chain_X[0]/Chain_Y[1]') vagy a lánc fejlécében ('Chain_X#fej').
   *  A hibák és a mezők azonosítója ezzel az úttal egyedi. */
  kulcs(kod: string, ut = '') { return `${this.aktForm}|${this.aktPeldany}|${ut}|${kod}`; }
  valtozott(m: NyMezo, ertek: any, ut = '') {
    this.piszkos = true;
    const h = this.ny.mezoHibak(m, ertek);
    const k = this.kulcs(m.kod, ut);
    if (h.length) this.mezoHiba[k] = h.join(' '); else delete this.mezoHiba[k];
    this.adoszamNez(m, ertek, k);
    this.motorFrissit();
  }

  /* ── ADÓSZÁM-ELLENŐRZÉS A NAV TÖRZSADATÁBAN ────────────────────────────────────────────
   *  A nyomtatványra írt adószámról a NAV megmondja, érvényes-e és kié (OSA `queryTaxpayer`).
   *  Ezt a szabálymotor nem tudja elvégezni – az a böngészőben, azonnal fut –, ezért külön
   *  kérdezzük meg, és TÁJÉKOZTATÁSKÉNT mutatjuk: a mező buboréksúgójában a cég neve, hibás
   *  adószámnál figyelmeztetés. A bevallás formai hibalistája változatlanul az XSD-ből jön. */
  adoszamInfo: Record<string, { rendben: boolean; szoveg: string }> = {};
  private adoszamIdozitok: Record<string, any> = {};
  private adoszamMezoE(m: NyMezo): boolean {
    const t = `${(m as any).technev || ''} ${(m as any).rovid || ''} ${m.cim || ''}`.toLowerCase();
    if (/adóazonosító|adoazonosito/.test(t)) return false;                 // az a magánszemély 10 jegyű jele
    return /adószám|adoszam/.test(t);
  }
  private adoszamNez(m: NyMezo, ertek: any, kulcs: string) {
    if (!this.adoszamMezoE(m) || !this.bev) return;
    const nyers = String(ertek ?? '').trim().toUpperCase();
    /*  A NAV törzsadata a MAGYAR adószámokat ismeri. A magyar közösségi adószám „HU" + a
     *  törzsszám, azt tehát meg tudjuk nézni; egy külföldi EU-adószámhoz (pl. „DE…") a VIES
     *  kellene – arról inkább nem mondunk semmit, mint hogy tévesen érvénytelennek jelezzük. */
    if (/^[A-Z]{2}/.test(nyers) && !nyers.startsWith('HU')) { delete this.adoszamInfo[kulcs]; return; }
    const sz = nyers.replace(/\D/g, '');
    clearTimeout(this.adoszamIdozitok[kulcs]);
    if (sz.length < 8) { delete this.adoszamInfo[kulcs]; return; }
    this.adoszamIdozitok[kulcs] = setTimeout(async () => {
      try {
        const r: any = await this.ny.get(`/adoszam/${this.bev.modul}/${this.bev.ugyfel_id}/${sz.slice(0, 8)}`);
        if (r?.skip) { delete this.adoszamInfo[kulcs]; return; }           // nincs NAV-kapcsolat – nem zavarunk
        const t = r?.torzs || {};
        this.adoszamInfo[kulcs] = t.ervenyes
          ? { rendben: true, szoveg: `A NAV szerint érvényes adószám: ${t.nev || t.rovid_nev || '—'}` }
          : { rendben: false, szoveg: 'A NAV törzsadatában ez az adószám nem érvényes.' };
      } catch { delete this.adoszamInfo[kulcs]; }                          // hálózati hiba: hallgatunk
    }, 700);
  }
  /** A mezőhöz tartozó adószám-üzenet (a súgóban és a jelzésen). */
  adoszamJelzes(m: NyMezo, ut = '') { return this.adoszamInfo[this.kulcs(m.kod, ut)] || null; }
  /** Az ÁNYK szerint számított mező (a táblázatos nézetben is csak olvasható). */
  szamitottE(m: NyMezo): boolean { return !!this.szamitottak[this.aktForm]?.has(m.kod); }
  /*  MIÉRT nem írható a mező. A szabály saját üzenete a legbeszédesebb („A mező csak akkor
   *  jelölhető, ha a képviselő az ügycsoport intézésére jogosult"); enélkül a felhasználó csak
   *  egy megmagyarázatlanul szürke dobozt lát, és hibának hiszi. */
  szamitottOk(m: NyMezo): string {
    return this.szamitottOkok[this.aktForm]?.get(m.kod)
      || 'Ezt a mezőt a nyomtatvány szabálya tölti ki, ezért nem írható.';
  }
  hibaja(m: NyMezo, ut = ''): string | null { return this.mezoHiba[this.kulcs(m.kod, ut)] || null; }
  mezoId(m: NyMezo, ut = '') { return 'm-' + m.kod + (ut ? '-' + ut.replace(/[^A-Za-z0-9]/g, '_') : ''); }
  fokuszal(m: NyMezo) { this.fokusz = m; }
  /** X-mező (logikai): szóköz / X kapcsol, Delete / Backspace töröl – mint az ÁNYK-ban. */
  /*  Jelölőnégyzet átváltása a táblázatos nézetben. A SZÁMÍTOTT mezőt nem engedjük átállítani:
   *  eddig engedtük, de a következő újraszámolás azonnal visszaírta a szabály szerinti értéket –
   *  a felhasználó annyit látott, hogy a pipa „magától eltűnik". A lapképen már így működött. */
  xValt(m: NyMezo, ad: any, ut = '') {
    if (this.szamitottE(m)) return;
    ad[m.kod] = (ad[m.kod] === true || ad[m.kod] === 'true') ? null : true;
    this.valtozott(m, ad[m.kod], ut);
  }
  xBill(ev: KeyboardEvent, m: NyMezo, ad: any, ut = '') {
    if (this.szamitottE(m)) return;
    const k = ev.key.toLowerCase();
    if (k === ' ' || k === 'x') { ev.preventDefault(); this.xValt(m, ad, ut); }
    else if (k === 'delete' || k === 'backspace') { ev.preventDefault(); ad[m.kod] = null; this.valtozott(m, null, ut); }
  }

  /* ── láncok (ismétlődő sorok) ── */
  lancSorok(l: NyCsomopont, ad: any): any[] {
    if (!ad) return [];
    if (!Array.isArray(ad[l.nev])) ad[l.nev] = [];
    return ad[l.nev];
  }
  lancFej(l: NyCsomopont, ad: any): any {
    if (!ad) return {};
    const k = l.nev + '#fej';
    if (!ad[k] || typeof ad[k] !== 'object') ad[k] = {};
    return ad[k];
  }
  lancUjSor(l: NyCsomopont, ad: any) {
    const s = this.lancSorok(l, ad);
    if (l.elemMax == null || s.length < l.elemMax) { s.push({}); this.piszkos = true; }
  }
  lancTorol(l: NyCsomopont, ad: any, i: number) { this.lancSorok(l, ad).splice(i, 1); this.piszkos = true; this.ellenoriz(false); }
  sorUt(ut: string, l: NyCsomopont, i: number) { return `${ut ? ut + '/' : ''}${l.nev}[${i}]`; }
  fejUt(ut: string, l: NyCsomopont) { return `${ut ? ut + '/' : ''}${l.nev}#fej`; }
  /** A lánc mezői laposan (a csoportokon át) – táblázatos megjelenítéshez. */
  lancMezok(l: NyCsomopont): NyMezo[] { return this.osszesMezo({ ...l, tipus: 'csoport' } as NyCsomopont); }
  /** Egyszerű lánc (csak mezők, legfeljebb 10) → táblázat; összetett (belső lánc, sok mező) → soronként kártya. */
  private lancModTar = new Map<string, boolean>();
  lancTablazatos(l: NyCsomopont): boolean {
    if (this.lancModTar.has(l.nev)) return this.lancModTar.get(l.nev)!;
    const vanBelsoLanc = (n: any): boolean => (n.elemek || []).some((e: any) => e.tipus === 'lanc' || vanBelsoLanc(e));
    const r = !vanBelsoLanc(l) && this.lancMezok(l).length <= 10;
    this.lancModTar.set(l.nev, r);
    return r;
  }
  feliratTipus(m: NyMezo): string {
    const r: string[] = [];
    if (m.adattipus === 'szam') r.push('szám');
    if (m.adattipus === 'datum') r.push('dátum');
    if (m.adattipus === 'kodlista') r.push(`kódlista (${m.ertekek?.length})`);
    if (m.adattipus === 'logikai') r.push('jelölőnégyzet');
    if (m.maxHossz) r.push(`max. ${m.maxHossz} karakter`);
    if (m.mintak?.length && m.adattipus !== 'szam') r.push(`minta: ${m.mintak[0]}`);
    if (m.kotelezo) r.push('kötelező');
    return r.join(' · ');
  }
  ertekCimke(e: { ertek: string; cim: string | null }) { return e.cim ? `${e.ertek} – ${e.cim}` : e.ertek; }

  /** Rácsbeli szélesség: rövid mező (irányítószám, dátum, szám) 1 oszlop, hosszú szöveg 2, hosszú jelölő a teljes sor. */
  racsSzeles(m: NyMezo): 'egy' | 'ketto' | 'sor' {
    const e = this.mezoElr(m).szeles;
    if (e === 'teljes') return 'sor';
    if (e === 'szeles') return 'ketto';
    if (e === 'kicsi' || e === 'kozepes') return 'egy';
    const cim = this.mezoCim(m);
    if (m.adattipus === 'logikai') return cim.length > 60 ? 'sor' : 'egy';
    if (m.adattipus === 'kodlista') return (m.ertekek || []).some((x) => (x.cim || x.ertek).length > 30) || cim.length > 90 ? 'ketto' : 'egy';
    if (m.adattipus === 'szoveg' && (!m.maxHossz || m.maxHossz > 40)) return 'ketto';
    return cim.length > 110 ? 'ketto' : 'egy';
  }
  racsSzakasz(sz: any): NyMezo[] | null { return sz.racs || null; }
  csomopontSzakasz(sz: any): NyCsomopont | null { return sz.csomopont || null; }

  /* ── elrendezés: felülírt felirat, súgó, karakterdobozok, szélesség ── */
  mezoElr(m: NyMezo): NyMezoElrendezes { return this.elr.mezok?.[m.kod] || {}; }
  mezoCim(m: NyMezo): string { return this.mezoElr(m).cim || m.cim; }
  blokkCim(b: NyCsomopont): string { return this.elr.blokkok?.[b.nev]?.cim || b.cim; }
  /** A szerkesztett mező elrendezése (a panel ezt köti; üres értékek mentéskor kiesnek). */
  get szerkMezo(): NyMezoElrendezes | null {
    if (!this.fokusz) return null;
    this.elr.mezok = this.elr.mezok || {};
    return this.elr.mezok[this.fokusz.kod] = this.elr.mezok[this.fokusz.kod] || {};
  }
  get szerkBlokk(): { cim?: string } | null {
    if (!this.blokk) return null;
    this.elr.blokkok = this.elr.blokkok || {};
    return this.elr.blokkok[this.blokk.nev] = this.elr.blokkok[this.blokk.nev] || {};
  }
  elrValtozott() { this.elrPiszkos = true; }
  /** Fogd és vidd a mezőcsoport rácsában: az új sorrend a csoport összes egyszerű mezőjére mentődik. */
  racsAthelyez(cs: NyCsomopont, racs: NyMezo[], ev: CdkDragDrop<NyMezo[]>) {
    if (ev.previousIndex === ev.currentIndex) return;
    moveItemInArray(racs, ev.previousIndex, ev.currentIndex);
    const t = this.tabla(cs);
    const kodok = t.szakaszok.flatMap((sz: any) => (sz.racs || []).map((m: NyMezo) => m.kod));
    this.elr.sorrend = { ...(this.elr.sorrend || {}), [cs.nev]: kodok };
    this.tablaTar.delete(cs.nev);
    this.elrPiszkos = true;
  }
  async elrMentes() {
    const tiszta = (o: Record<string, any>) => Object.fromEntries(Object.entries(o || {})
      .map(([k, v]) => [k, Object.fromEntries(Object.entries(v || {}).filter(([, x]) => x !== '' && x != null && x !== 0))])
      .filter(([, v]) => Object.keys(v as object).length));
    try {
      await this.ny.put(`/sablon/${this.modell!.kod}/elrendezes`, { elrendezes: { mezok: tiszta(this.elr.mezok || {}), blokkok: tiszta(this.elr.blokkok || {}), sorrend: this.elr.sorrend || {} } });
      this.elrPiszkos = false;
      this.jelez('Elrendezés mentve – minden bevallásnál így látszik.', 'success');
    } catch (e: any) { this.jelez(e?.error?.error || 'Nem sikerült menteni az elrendezést.', 'danger'); }
  }

  /* ── ÁNYK-lapkép: .enyk oldalak ↔ a bevallás adata ──
   *  A mezők kötése: az XSD-ben is meglévő fid a lap-példányon vagy (dinamikus lapon) a lap
   *  láncának aktuális során; a láncon belüli sortartomány (pl. 520–522. = FEOR 3 sora) a belső
   *  lánc soraira; a csak ÁNYK-ban létező (számított, lapjelző) mezők egy segédtárba, csak olvasható. */
  /*  A lapkép ↔ adat kötése (anyk-kotes.ts): melyik ÁNYK-mező melyik XSD-mezőt jelenti, hány
   *  példánya van egy dinamikus lapnak, mely lapok kitöltöttek. A kötegelt nyomtatás ugyanezt
   *  használja, hogy a papírkép és a képernyő ne csúszhasson el egymástól. */
  kotes: AnykKotes | null = null;
  private kotesEpit() { this.kotes = this.modell ? new AnykKotes(this.modell, this.enyk, this.motor) : null; }
  enykForm(formNev: string): any { return this.enyk?.formok?.[formNev.replace(/^Form_/, '')] || null; }
  /** Az aktuális részbizonylat mezőinek ÁNYK-metaadata (mátrixhivatkozás, kötelezőség …). */
  get enykMetak(): Record<string, any> { return this.enykForm(this.aktForm)?.metak || {}; }
  get lapok(): any[] { return this.enykForm(this.aktForm)?.oldalak || []; }
  get lap(): any { return this.lapok[this.aktLap] || null; }
  lapLanca(formNev: string, oldal: any): string | null { return this.kotes?.lapLanca(formNev, oldal) || null; }
  anykHely(formNev: string, peldany: any, oldal: any, din: number, fid: string): AnykHely | null {
    return this.kotes?.hely(formNev, peldany, oldal, din, fid) || null;
  }
  anykMezo = (fid: string): NyMezo | undefined => this.kotes?.mezo(this.aktForm, fid);
  anykKotes = (fid: string): AnykHely | null => (this.lap ? this.anykHely(this.aktForm, this.peldany, this.lap, this.aktDin, fid) : null);
  /** A dinamikus lap példányainak száma (a lánc sorai; legalább 1). */
  dinDb(formNev: string, peldany: any, oldal: any): number { return this.kotes?.dinDb(formNev, peldany, oldal) || 1; }
  private dinIdxTar: number[] = [];
  dinIndexek(formNev: string, peldany: any, oldal: any): number[] {
    const n = this.dinDb(formNev, peldany, oldal);
    if (this.dinIdxTar.length !== n) this.dinIdxTar = Array.from({ length: n }, (_, i) => i);
    return this.dinIdxTar;
  }
  lapra2(formNev: string, peldany: number, lap: number, din = 0) {
    const valt = formNev !== this.aktForm || peldany !== this.aktPeldany;
    this.aktForm = formNev; this.aktPeldany = peldany; this.aktLap = lap; this.aktDin = din; this.fokusz = null;
    if (valt) this.lapAllapotFrissit();
    document.querySelector('.any-lap')?.scrollTo({ top: 0 });
  }
  ujDinLap(formNev: string, peldany: any, oldal: any) {
    const ml = this.motor ? this.kotes?.motorLapLanc(formNev, oldal) : null;
    if (ml) {
      // Egy lappéldány a lánc `laponkent` sora (pl. 08M-06: 4 sor) – a meglévőt kiegészítjük, majd egy új lapnyit adunk.
      if (!Array.isArray(peldany[ml.nev])) peldany[ml.nev] = [];
      const db = this.dinDb(formNev, peldany, oldal);
      while (peldany[ml.nev].length < (db + 1) * ml.laponkent) peldany[ml.nev].push({});
      this.piszkos = true;
      this.aktDin = db;
      return;
    }
    const l = this.lapLanca(formNev, oldal);
    if (!l) return;
    if (!Array.isArray(peldany[l])) peldany[l] = [];
    peldany[l].push({});
    this.piszkos = true;
    this.aktDin = peldany[l].length - 1;
  }
  anykValtozott(ev: { fid: string; m?: NyMezo; ertek: any }) {
    this.piszkos = true;
    if (ev.m) {
      const h = this.ny.mezoHibak(ev.m, ev.ertek);
      const k = `${this.aktForm}|${this.aktPeldany}|${this.aktDin}|${ev.fid}`;
      if (h.length) this.lapHiba[k] = h.join(' '); else delete this.lapHiba[k];
    }
    this.motorFrissit();
  }

  /* ── ÁNYK-szabálymotor (anyk-szabalymotor.ts) ──
   *  A sablonból CSAK a kitöltést segítő szabályokat futtatjuk: a számított mezők (field_calc)
   *  értékét és a lapok megnyithatóságát (page_check). A mezőellenőrzéseket NEM: a bevallás
   *  hibalistája kizárólag a hatályos XSD-n alapul (lásd ellenoriz()). */
  motor: AnykSzabalymotor | null = null;
  /** Formonként a számított (csak olvasható) ENYK-mezők. */
  szamitottak: Record<string, Set<string>> = {};
  /** Formonként: fid → miért zárt a mező (a számító szabály üzenete). */
  szamitottOkok: Record<string, Map<string, string>> = {};
  /** Lapnév → megnyitható-e (és ha nem, miért) az aktuális példányon. */
  lapAllapot: Record<string, { nyithato: boolean; ok?: string }> = {};
  private motorIdozito: any = null;
  private motorIndit() {
    try {
      this.motor = new AnykSzabalymotor(this.enyk, this.modell);
      this.kotes?.motorral(this.motor);
      this.szamitottak = {};
      this.lookupListak();           // kódkeresők választéka (ÁNYK `lookup_create`)
      this.munkanaptarBetolt();      // munkanap-áthelyezések (a határidő-számító szabályokhoz)
      this.szamol();                 // a számolás tölti fel a ténylegesen számított mezők halmazát
      this.lapAllapotFrissit();
      this.ellenoriz(false);
    } catch (e) {
      console.error('[anyk-szabalymotor] nem indult el', e);
      this.motor = null;
      this.kotes?.motorral(null);
    }
  }
  /*  MUNKANAPTÁR. Néhány nyomtatvány a befizetési határidőt úgy számolja, hogy az első olyan
   *  napot keresi, amelyik MUNKANAP (26RENDNY). A hétvégét és a munkaszüneti napokat a motor
   *  maga tudja; az évenkénti munkanap-áthelyezéseket viszont csak adatból – azt innen töltjük
   *  be. Ha nem érhető el, a számítás a hétvégékkel és az ünnepekkel akkor is helyes marad. */
  private async munkanaptarBetolt() {
    const ev = Number(this.bev?.ev) || new Date().getFullYear();
    try {
      const r: any = await this.ny.get(`/munkanaptar/${ev}`);
      this.motor?.munkanaptarBeallit(r?.felulirasok || {});
      this.szamol();
    } catch { /* marad a képlettel számolt naptár */ }
  }

  /*  KÓDKERESŐ MEZŐK VÁLASZTÉKA. Néhány lista nem az XSD-ben és nem is a mező META-jában áll,
   *  hanem az ÁNYK `lookup_create` szabályában (ilyen az ÚJEGYKE „képviselet jogcíme" és a
   *  T201T TEÁOR-mezője). Ezeket a motor olvassa ki a sablonból; itt rátesszük a modell
   *  mezőire javaslatként, így a lapkép és a táblázatos nézet is felkínálja őket. A mezőt
   *  ettől még szabadon lehet gépelni – a formai ellenőrzés az XSD szerint megy. */
  private lookupListak() {
    if (!this.motor || !this.kotes) return;
    for (const f of this.formok) {
      let lista: Map<string, { ertek: string; cim: string | null }[]>;
      try { lista = this.motor.lookupListak(f.nev); } catch { continue; }
      for (const [fid, opciok] of lista) {
        const m: any = this.kotes.mezo(f.nev, fid);
        if (!m || m.ertekek?.length || m.javasoltErtekek?.length) continue;
        m.javasoltErtekek = opciok;
      }
    }
  }

  /*  A SZABÁLYMOTOR FUTTATÁSA – a technikai mezők értékét megőrizve.
   *
   *  Az ÁNYK néhány technikai mezőt maga számol (pl. „Benyújtott 23-as pótlapok száma"). Amit a
   *  motor ki tud számolni, az övé. Amit nem (nálunk pl. a dinamikus lapok számlálása), azt a
   *  kiszolgáló előtöltése tölti a lapkép kedvéért – azt nem hagyjuk elveszni: a számolás után
   *  visszaírjuk, ha a motor üresen hagyta. Ezek a mezők a beküldött adatba amúgy sem kerülnek. */
  private auxErtekek(): { obj: any; kulcs: string; ertek: any }[] {
    const ki: { obj: any; kulcs: string; ertek: any }[] = [];
    const bejar = (o: any) => {
      if (!o || typeof o !== 'object') return;
      if (o['#aux'] && typeof o['#aux'] === 'object') {
        for (const [k, v] of Object.entries(o['#aux'])) if (v !== null && v !== undefined && v !== '') ki.push({ obj: o['#aux'], kulcs: k, ertek: v });
      }
      for (const v of Object.values(o)) if (Array.isArray(v)) v.forEach(bejar); else if (v && typeof v === 'object') bejar(v);
    };
    for (const lista of Object.values(this.adat || {})) (lista as any[])?.forEach?.(bejar);
    return ki;
  }
  private szamol() {
    if (!this.motor) return;
    const elmentett = this.auxErtekek();
    try { this.motor.szamol(this.adat, { feltetelesKitoltes: false }); } catch { /* hibatűrő */ }
    for (const e of elmentett) {
      const most = e.obj[e.kulcs];
      if (most === null || most === undefined || most === '') e.obj[e.kulcs] = e.ertek;
    }
    /*  A csak olvasható mezők köre MINDEN számolás után újra eldől. Az ÁNYK-ban sok mező csak
     *  FELTÉTELESEN számított (az ÚJEGYKE főlapján például az ügycsoport-jelölők csak bizonyos
     *  jogcímeknél állnak be maguktól); amíg a képlet nem ad értéket, a mezőt ki kell tudni
     *  tölteni. Korábban a puszta képlet-lét zárta le őket – így az ÚJEGYKE „D" blokkja
     *  kitölthetetlen volt, pedig pont ott kell megadni, mire szól a meghatalmazás. */
    try {
      for (const f of this.formok) {
        this.szamitottak[f.nev] = this.motor.szamitottAktualis(f.nev);
        this.szamitottOkok[f.nev] = this.motor.szamitottOkok(f.nev);
      }
    } catch { /* marad a korábbi */ }
  }

  /** Adatváltozás után: számított mezők újraszámolása, lapengedélyek (kicsit késleltetve, gépelés közben ne fusson minden leütésre). */
  motorFrissit() {
    if (!this.motor) return;
    clearTimeout(this.motorIdozito);
    this.motorIdozito = setTimeout(() => {
      this.szamol();
      this.lapAllapotFrissit();
    }, 250);
  }
  lapAllapotFrissit() {
    if (!this.motor || !this.aktForm) { this.lapAllapot = {}; return; }
    try { this.lapAllapot = this.motor.lapAllapot(this.adat, this.aktForm, this.aktPeldany); } catch { this.lapAllapot = {}; }
  }
  lapNyithato(o: any): boolean { return this.lapAllapot[o.nev]?.nyithato !== false; }
  szamitottLap(): Set<string> { return this.szamitottak[this.aktForm] || new Set(); }
  szamitottOkLap(): Map<string, string> { return this.szamitottOkok[this.aktForm] || new Map(); }
  lapHiba: Record<string, string> = {};
  get lapHibaElotag() { return `${this.aktForm}|${this.aktPeldany}|${this.aktDin}|`; }
  anykFokusz(ev: { fid: string; m?: NyMezo }) { this.fokusz = ev.m || ({ tipus: 'mezo', nev: ev.fid, kod: ev.fid, cim: 'ÁNYK-segédmező (nem kerül a beküldött XML-be)', adattipus: 'szoveg' } as NyMezo); }
  /** A nyomtatandó lapok: formonként, példányonként, dinamikus lapnál lappéldányonként. */
  nyomtatandok(uresekNelkul = true) { return this.kotes?.nyomtatandok(this.adat, { uresekNelkul }) || []; }
  /** A nyomtatás lapjai – EGYSZER, a nyomtatás indításakor számolva (a getter minden változásérzékeléskor
   *  új listát adna, amitől a lapok újra és újra felépülnének). */
  nyomtatasLapok: { oldal: any; arany: number; hely: (fid: string) => AnykHely | null; mezo: (fid: string) => NyMezo | undefined; metak: Record<string, any> }[] = [];

  /* ── ellenőrzés, mentés, XML ── */
  ellenoriz(nyit = true) {
    if (!this.modell) return;
    /*  AZ ELLENŐRZÉS ALAPJA KIZÁRÓLAG A HATÁLYOS XSD (a NAV sémája).
     *  Az ÁNYK-sablon tartalmi szabályait nem kérjük számon: az ÁNYK helyi szabályrendszere
     *  szigorúbb a NAV gépi (M2M) ellenőrzésénél, ezért olyan tételeket is hibának jelzett,
     *  amelyeket a NAV hibátlannak fogadott el. A sablon szabálymotorja továbbra is fut, de csak a
     *  számított mezőkért és a lapok megnyithatóságáért – hibát nem ad. */
    const xsd = this.ny.ellenoriz(this.modell, this.adat).map((h) => this.lapkepre(h));
    if (this.motor) {
      if (nyit) this.szamol();
      this.lapAllapotFrissit();
    }
    this.hibak = xsd;
    /*  ÜRES NYOMTATVÁNY. A séma szerint szinte minden mező opcionális, ezért egy üres bevallás
     *  „hibátlan" – a NAV viszont nem ezt várja. Ne mondjuk rá, hogy rendben van. */
    if (!this.vanBarmiAdat()) {
      this.hibak = [{ form: this.aktForm, peldany: 0, ut: '', mezo: null, cim: '', lap: '',
        uzenet: 'A nyomtatvány üres – nincs kitöltött adat. Beküldés előtt töltsd ki (vagy használd az Előtöltést).',
        szint: 'warning' }, ...xsd];
    }
    this.mezoHiba = {};
    this.lapHiba = {};
    for (const h of this.hibak) {
      if (h.mezo) {
        const k = `${h.form}|${h.peldany}|${h.ut || ''}|${h.mezo}`;
        this.mezoHiba[k] = (this.mezoHiba[k] ? this.mezoHiba[k] + ' ' : '') + h.uzenet;
      }
      if (h.fid) {
        const k = `${h.form}|${h.peldany}|${h.lapIndex || 0}|${h.fid}`;
        this.lapHiba[k] = (this.lapHiba[k] ? this.lapHiba[k] + ' ' : '') + h.uzenet;
      }
    }
    const hibaDb = this.hibak.filter((h) => h.szint !== 'warning').length;
    const figy = this.hibak.length - hibaDb;
    if (nyit) {
      this.hibalistaNyitva = true;
      this.jelez(this.hibak.length ? `${hibaDb} hiba${figy ? `, ${figy} figyelmeztetés` : ''}` : 'Hibátlan', hibaDb ? 'warning' : 'success');
    }
  }
  /*  XSD-hiba a LAPKÉPEN.
   *  Az XSD a mező technikai nevét adja; a nyomtatványképen viszont ÁNYK-mezőazonosító (fid) van.
   *  Megkeressük, melyik lapon és melyik fid-en látszik a mező, hogy a hibalistából oda lehessen
   *  ugrani és a mező pirosan jelenjen meg a lapon is. */
  /** Van-e egyáltalán kitöltött mező a bevallásban (az azonosító adatokon túl is)? */
  private vanBarmiAdat(): boolean {
    let db = 0;
    const bejar = (o: any) => {
      if (!o || typeof o !== 'object') return;
      for (const [k, v] of Object.entries(o)) {
        if (k === '#aux') continue;
        if (Array.isArray(v)) v.forEach(bejar);
        else if (v && typeof v === 'object') bejar(v);
        else if (v !== null && v !== undefined && v !== '' && v !== false) db += 1;
      }
    };
    bejar(this.adat);
    // Az adószám/név a törzsből magától bekerül – önmagában még nem kitöltött nyomtatvány.
    return db > 3;
  }
  private xsdFidTar = new Map<string, { fid: string; lap: string; oldalIndex: number; lanc: string | null } | null>();
  private xsdFid(formNev: string, mezo: string) {
    const k = formNev + '|' + mezo;
    if (!this.xsdFidTar.has(k)) {
      let t: { fid: string; lap: string; oldalIndex: number; lanc: string | null } | null = null;
      const oldalak = this.enykForm(formNev)?.oldalak || [];
      keres: for (let oi = 0; oi < oldalak.length; oi++) {
        for (const e of oldalak[oi].elemek) {
          if (e.t !== 'mezo') continue;
          const kod = this.kotes?.mezo(formNev, e.fid)?.kod;
          if (kod === mezo) { t = { fid: e.fid, lap: oldalak[oi].nev, oldalIndex: oi, lanc: this.lapLanca(formNev, oldalak[oi]) }; break keres; }
        }
      }
      this.xsdFidTar.set(k, t);
    }
    return this.xsdFidTar.get(k)!;
  }
  private lapkepre(h: NyHiba): NyHiba {
    if (!this.enyk || !h.mezo) return h;
    const t = this.xsdFid(h.form, h.mezo);
    if (!t) return h;
    // Dinamikus lapon a hiba a lánc adott sorához (lappéldányához) tartozik.
    const m = t.lanc ? String(h.ut || '').match(new RegExp(`(?:^|/)${t.lanc}\\[(\\d+)\\]`)) : null;
    return { ...h, fid: t.fid, anykLap: t.lap, lapIndex: m ? Number(m[1]) : 0 };
  }
  get hibaSzam(): number { return this.hibak.filter((h) => h.szint !== 'warning').length; }
  hibahoz(h: NyHiba) {
    // A lapképen: a hiba lapjára (és a dinamikus lap példányára) ugrunk.
    if (h.anykLap && this.enyk && this.nezet === 'lapkep') {
      const oi = (this.enykForm(h.form)?.oldalak || []).findIndex((o: any) => o.nev === h.anykLap);
      if (oi >= 0) {
        this.lapra2(h.form, h.peldany, oi, h.lapIndex || 0);
        this.lapAllapotFrissit();
        setTimeout(() => {
          const el = h.fid ? document.querySelector(`[data-fid="${h.fid}"] input, [data-fid="${h.fid}"] select, [data-fid="${h.fid}"] .anyk-x`) as HTMLElement | null : null;
          el?.scrollIntoView({ block: 'center' }); el?.focus();
        }, 60);
        return;
      }
    }
    const f = this.form(h.form);
    const bi = (f?.elemek || []).findIndex((b: any) => (b.lap || b.cim) === h.lap);
    this.lapra(h.form, h.peldany, Math.max(0, bi));
    setTimeout(() => {
      const id = h.mezo ? 'm-' + h.mezo + (h.ut ? '-' + h.ut.replace(/[^A-Za-z0-9]/g, '_') : '') : '';
      const el = id ? document.getElementById(id) as HTMLElement | null : null;
      el?.scrollIntoView({ block: 'center' }); el?.focus();
    }, 60);
  }
  async mentes() {
    if (!this.bev || this.ment) return;
    if (this.zarTilt) { this.jelez('Most nem menthető – lásd a figyelmeztetést a lap tetején.', 'warning'); return; }
    this.ment = true;
    try {
      const r: any = await this.ny.put('/bevallas/' + this.bev.id, { adat: this.adat });
      this.bev.allapot = r.allapot; this.piszkos = false;
      this.ellenoriz(false);
      this.jelez(r.hibak.length ? `Mentve – ${r.hibak.length} hibával` : 'Mentve, hibátlan.', r.hibak.length ? 'warning' : 'success');
    } catch (e: any) { this.jelez(e?.error?.error || 'Nem sikerült menteni.', 'danger'); }
    finally { this.ment = false; }
  }
  m2mIgazolas() {
    this.ny.fajlMegnyit(`/api/nav-nyomtatvany/bevallas/${this.bev.id}/m2m-igazolas.pdf`, `${this.bev.kod} – igazolás a gépi beküldésről`);
  }
  /*  HELYESBÍTÉS / ÖNELLENŐRZÉS ebből a bevallásból: új bevallás az adatok másolatával, a „Bevallás
   *  jellege” mezőben H vagy O jelöléssel – utána azt nyitjuk meg. */
  async javitoKeszit() {
    if (this.piszkos) await this.mentes();
    const b = this.bev;
    const a = await this.alert.create({
      header: 'Javító bevallás',
      message: `A(z) ${b.kod} adataiból új bevallás készül. Önellenőrzésnél az önellenőrzési pótlék és a különbözet `
        + 'sorait a nyomtatvány szabályai szerint töltsd ki – az ellenőrzés jelzi, mi hiányzik.',
      inputs: [
        { type: 'radio', label: 'Helyesbítés (adózói javítás – H)', value: 'H', checked: b.jelleg !== 'O' },
        { type: 'radio', label: b.jelleg === 'O' ? 'Ismételt önellenőrzés (O)' : 'Önellenőrzés (O)', value: 'O', checked: b.jelleg === 'O' },
      ],
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Létrehozás', role: 'ok' }],
    });
    await a.present();
    const v = await a.onDidDismiss();
    if (v.role !== 'ok' || !v.data?.values) return;
    try {
      const r: any = await this.ny.post(`/bevallas/${b.id}/javito`, { jelleg: v.data.values });
      this.jelez(r.ismetelt ? 'Ismételt önellenőrzés létrehozva.' : (r.jelleg === 'O' ? 'Önellenőrzés létrehozva.' : 'Helyesbítés létrehozva.'), 'success');
      //  Ugyanez a képernyő nyílik más azonosítóval – a komponens nem töltene újra, ezért kerülővel navigálunk.
      const cel = [this.modul === 'BER' ? '/ber/bevallas' : this.modul === 'GEPJARMU' ? '/gepjarmu/bevallas' : '/naplo/bevallas', r.id];
      await this.router.navigateByUrl('/', { skipLocationChange: true });
      await this.router.navigate(cel);
    } catch (e: any) { this.jelez(e?.error?.error || 'Nem sikerült létrehozni.', 'danger'); }
  }
  async xmlLetolt() {
    if (this.piszkos) await this.mentes();
    try {
      const xml = await this.ny.xml(this.bev.id);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([xml], { type: 'application/xml' }));
      a.download = `${this.bev.kod}_${this.bev.ev}${this.bev.ho ? String(this.bev.ho).padStart(2, '0') : ''}.xml`;
      a.click(); URL.revokeObjectURL(a.href);
    } catch (e: any) {
      let uz = 'Nem sikerült előállítani az XML-t.';
      try { uz = JSON.parse(e?.error)?.error || uz; } catch { /* szöveges válasz */ }
      this.jelez(uz, 'danger'); this.ellenoriz(true);
    }
  }
  /**
   *  Nyomtatás: ÁNYK-sablonnal a kitöltött lapok nyomtatványképe, laponként A4 – anélkül a táblázatos nézet.
   *  A képernyőt nem nyomtatjuk (az Ionic-keret menüje, fejléce is papírra kerülne, a görgetett tartalom
   *  levágódna): a lapok egy rejtett tárolóban renderelődnek, és a kitöltött értékekkel együtt egy
   *  különálló keretbe (iframe) másolva nyomtatjuk – ugyanúgy, mint a NAPLO nyomtatása (naplo-nyomtatas.ts).
   */
  /*  A fejléc címere ADAT-URI-ként: a nyomtatási keret rejtett dokumentum, a böngésző a benne
   *  lévő képek betöltését elhalaszthatja – beágyazva nincs mit betölteni. Egyszer kérjük le. */
  private logoAdatUri: string | null = null;
  private async logoBeagyaz(): Promise<string | null> {
    if (this.logoAdatUri === null) this.logoAdatUri = await kepAdatUri(this.logo);
    return this.logoAdatUri;
  }

  /*  NYOMTATÁS.
   *  Alapesetben a TELJES bevallás megy papírra – a 08-as és társai kötegelt nyomtatványok: egy
   *  főlap és mellette annyi M-lap (pótlap), ahány dolgozóé –, az üres lapokat kihagyva. Aki csak
   *  a képernyőn nézett lapot akarja, vagy üres nyomtatványt nyomtatna, azt is választhatja. */
  nyomtatasMod: 'koteg' | 'lap' | 'mind' = 'koteg';
  async nyomtat() {
    if (!this.enyk) { window.print(); return; }
    const a = await this.alert.create({
      header: 'Nyomtatás',
      inputs: [
        { type: 'radio', label: 'A teljes bevallás (üres lapok nélkül)', value: 'koteg', checked: this.nyomtatasMod === 'koteg' },
        { type: 'radio', label: 'Csak a most nézett lap', value: 'lap', checked: this.nyomtatasMod === 'lap' },
        { type: 'radio', label: 'Minden lap, üresen is', value: 'mind', checked: this.nyomtatasMod === 'mind' },
      ],
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Nyomtatás', role: 'ok' }],
    });
    await a.present();
    const v = await a.onDidDismiss();
    if (v.role !== 'ok') return;
    this.nyomtatasMod = (v.data?.values || this.nyomtatasMod) as any;
    await this.nyomtatasIndit(this.nyomtatasMod);
  }
  private async nyomtatasIndit(mod: 'koteg' | 'lap' | 'mind') {
    const logoUri = await this.logoBeagyaz();
    const tetelek = mod === 'lap'
      ? (this.lap ? [{ formNev: this.aktForm, peldany: this.peldany, oldal: this.lap, din: this.aktDin }] : [])
      : this.nyomtatandok(mod === 'koteg');
    if (!tetelek.length) { this.jelez('Nincs nyomtatható lap.', 'warning'); return; }
    this.nyomtatasLapok = tetelek.map((t) => ({
      oldal: t.oldal,
      arany: lapArany(t.oldal),
      hely: (fid: string) => this.anykHely(t.formNev, t.peldany, t.oldal, t.din, fid),
      mezo: (fid: string) => this.kotes?.mezo(t.formNev, fid),
      metak: this.enykForm(t.formNev)?.metak || {},
    }));
    this.nyomtatasFut = true;
    this.jelez(`${this.nyomtatasLapok.length} lap nyomtatása…`, 'success');
    // A lapok felépülésére várunk, csak utána másoljuk a nyomtatási keretbe.
    setTimeout(async () => {
      const forras = document.querySelector('.any-nyomtat') as HTMLElement | null;
      if (!forras) { this.nyomtatasFut = false; return; }
      const cim = `${this.modell?.kod || ''} – ${this.bev?.cim || ''}`;
      try { await keretbeNyomtat(forras, cim, { url: this.logo, adatUri: logoUri }); }
      finally { this.nyomtatasFut = false; }
    }, 300);
  }

  async vissza() {
    //  Menteni csak a zár birtokában lehet – zár nélkül nincs mit felkínálni.
    if (this.piszkos && !this.zarTilt) {
      const a = await this.alert.create({ header: 'Nem mentett változások', message: 'Mentsem a bevallást kilépés előtt?',
        buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Nem', role: 'nem' }, { text: 'Mentés', role: 'ment' }] });
      await a.present();
      const r = (await a.onDidDismiss()).role;
      if (r === 'cancel' || r === 'backdrop') return;
      if (r === 'ment') await this.mentes();
    }
    this.zarElenged();
    const vissza = this.modul === 'BER' ? '/ber/bevallasok' : this.modul === 'GEPJARMU' ? '/gepjarmu/cegautoado?ful=bevallas' : '/naplo/bevallasok';
    if (!this.keret.modulba(this.modul, vissza)) this.router.navigateByUrl(vissza);
  }

  @HostListener('window:keydown', ['$event'])
  billentyu(e: KeyboardEvent) {
    const mod = e.ctrlKey || e.metaKey;
    if (mod && e.key.toLowerCase() === 's') { e.preventDefault(); this.mentes(); }
    /*  A böngésző saját nyomtatása a KÉPERNYŐT vinné papírra (a most nézett lapot, levágva),
     *  ezért a Ctrl+P-t is a nyomtatványkép-nyomtatásra vezetjük. */
    else if (mod && e.key.toLowerCase() === 'p') { e.preventDefault(); this.nyomtat(); }
    else if (e.key === 'F6') { e.preventDefault(); this.ellenoriz(true); }
    else if (mod && e.key === 'PageDown') { e.preventDefault(); this.kovetkezo(1); }
    else if (mod && e.key === 'PageUp') { e.preventDefault(); this.kovetkezo(-1); }
  }

  /*  Előtöltés (backend: POST /bevallas/:id/kitolt). KÉTFÉLE:
   *   - modul-adatból (pl. a 2608 a bérszámfejtésből): a teljes adatot ÚJRAÉPÍTI – ezért kérdez;
   *   - törzsadatból (minden más nyomtatvány): csak az ÜRES azonosító mezőket tölti ki, a beírt
   *     adatok megmaradnak – itt fölösleges (és félrevezető) lenne elvesztéssel riogatni. */
  async elotolt(kerdez: boolean) {
    if (!this.bev || this.elotoltFut || this.zarTilt) return;
    if (kerdez && !this.kitoltesKiegeszit) {
      const a = await this.alert.create({ header: 'Előtöltés', message: `A nyomtatvány adatait újraépítem ebből: ${this.kitoltheto}. A kézi módosítások elvesznek. Folytatod?`,
        buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Előtöltés', role: 'ok' }] });
      await a.present();
      if ((await a.onDidDismiss()).role !== 'ok') return;
    }
    this.elotoltFut = true;
    try {
      const r: any = await this.ny.post(`/bevallas/${this.bev.id}/kitolt`, {});
      this.adat = r.adat || {};
      for (const f of this.modell!.formok) if (!Array.isArray(this.adat[f.nev])) this.adat[f.nev] = f.min >= 1 ? [{}] : [];
      this.bev.kitoltve = new Date().toISOString();
      this.aktPeldany = 0; this.aktDin = 0;
      if (this.enyk) this.kotesEpit();
      this.ellenoriz(false);
      this.piszkos = false;
      const k = r.kitoltes || {};
      this.jelez(this.kitoltesKiegeszit
        ? (k.mezok ? `Kitöltve a törzsadatból: ${(k.lista || []).slice(0, 6).join(', ')}${(k.lista || []).length > 6 ? ' …' : ''} (${k.mezok} mező).`
                   : 'A törzsadatból nem volt mit kitölteni – vagy már ki van töltve, vagy az ügyfélnél nincs meg az adat.')
        : `Előtöltve (${this.kitoltheto}): ${k.mlapok != null ? k.mlapok + ' M-lap, ' : ''}${k.mezok || 0} mező.`,
        k.mezok || !this.kitoltesKiegeszit ? 'success' : 'warning');
      for (const f of k.figyelmeztetesek || []) (await this.toast.create({ message: f, duration: 5000, color: 'warning', position: 'top' })).present();
    } catch (e: any) { this.jelez(e?.error?.error || 'Nem sikerült előtölteni.', 'danger'); }
    finally { this.elotoltFut = false; }
  }

  technikaiValt() {
    this.technikaiMutat = !this.technikaiMutat;
    try { localStorage.setItem('nyomtatvany-technikai', this.technikaiMutat ? '1' : '0'); } catch { /* privát mód */ }
  }

  /* ── NAV-beküldés (M2M). A beállítás a Szervíz → NAV-kapcsolat képernyőn van; itt csak használjuk. ── */
  private async m2mBetolt() {
    try {
      //  a hitelesítő a BEVALLÁS ügyfeléhez tartozik – az lehet a másik modulé is (párcég)
      const r: any = await this.ny.get(`/m2m/${this.bev.modul || this.modul}/${this.bev.ugyfel_id}`);
      this.m2mKornyezet = r?.hasznalt === 'eles' ? 'eles' : 'teszt';
    } catch { /* a gomb ilyenkor a tesztet mutatja, a kiszolgáló úgyis eldönti */ }
  }
  async navEllenorzes() {
    if (this.piszkos) await this.mentes();
    if (this.gepiNemMegy && !(await this.gepiMegis())) return;
    this.m2mFut = 'ellenorzes';
    try {
      const r: any = await this.ny.post(`/bevallas/${this.bev.id}/nav-ellenorzes`, { eroltetett: this.gepiNemMegy });
      const v = r.valasz || {};
      this.jelez(r.ok ? `NAV-ellenőrzés (${r.kornyezet}, ${r.bizonylat}): ${v.bizonylatStatusz || 'rendben'}${r.tanusitvany ? ' – tanúsítvány megvan' : ''}`
        + (r.helyi_hibak ? ` · a helyi ellenőrzés ${r.helyi_hibak} hibát jelez` : '')
        : `NAV-ellenőrzés: ${v.magyarazat || v.resultMessage || v.bizonylatStatusz || 'hibás'}`, r.ok ? 'success' : 'warning');
      if (v.ugyAzonosito) this.bev.m2m_azonosito = v.ugyAzonosito;
    } catch (e: any) { this.jelez(e?.error?.error || 'A NAV-ellenőrzés nem sikerült.', 'danger'); }
    finally { this.m2mFut = null; }
  }
  async bekuldes() {
    if (this.zarTilt) return;
    if (this.piszkos) await this.mentes();
    if (this.gepiNemMegy && !(await this.gepiMegis())) return;
    const eles = this.m2mKornyezet === 'eles';
    const a = await this.alert.create({
      header: eles ? 'Beküldés a NAV ÉLES rendszerébe' : 'Beküldés a NAV tesztkörnyezetébe',
      message: eles
        ? `A(z) ${this.bev.kod} bevallás VALÓDI beküldése következik, jogkövetkezménnyel. Biztosan beküldöd?`
        : `A(z) ${this.bev.kod} bevallás a NAV tesztkörnyezetébe (m2m-dev) megy – nincs jogkövetkezménye, `
          + 'és akkor is elküldhető, ha a helyi ellenőrzés még hibát jelez.',
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: eles ? 'Beküldöm élesbe' : 'Beküldés', role: 'ok' }],
    });
    await a.present();
    if ((await a.onDidDismiss()).role !== 'ok') return;
    this.m2mFut = 'bekuldes';
    try {
      const r: any = await this.ny.post(`/bevallas/${this.bev.id}/bekuldes`, { eles, eroltetett: this.gepiNemMegy });
      const v = r.valasz || {};
      if (r.ok) { this.bev.allapot = 'bekuldve'; this.bev.m2m_azonosito = v.ugyAzonosito || this.bev.m2m_azonosito; this.zarElenged(); }
      this.jelez(r.ok ? `Beküldve (${r.kornyezet}, ${r.bizonylat}) – ügyazonosító: ${v.ugyAzonosito || '—'}`
        : `A NAV nem fogadta: ${v.magyarazat || v.resultMessage || v.bizonylatStatusz || 'ismeretlen hiba'}`, r.ok ? 'success' : 'danger');
    } catch (e: any) { this.jelez(e?.error?.error || 'A beküldés nem sikerült.', 'danger'); }
    finally { this.m2mFut = null; }
  }
  /*  A nyomtatványhoz még nincs XPath a NAV-nál: megmondjuk, és csak kifejezett kérésre hívjuk a
   *  NAV-ot (ha a NAV a tárhely frissítése előtt kapcsolná be). */
  private async gepiMegis(): Promise<boolean> {
    const a = await this.alert.create({
      header: 'A NAV ezt még nem fogadja gépi úton',
      message: `A(z) ${this.bev.kod} nyomtatványhoz a NAV sablontárában még nincs meg az ellenőrző XPath-állomány, `
        + 'és amíg nincs, a NAV „a bizonylattípus – bizonylatverzió kombináció nem létezik” választ ad. '
        + 'Addig töltsd le az XML-t a fenti gombbal, és add be az ONYA-ban (online nyomtatványkitöltő, XML-betöltéssel). '
        + 'A rendszer naponta figyeli, és amint megjelenik, ez az üzenet eltűnik.',
      buttons: [{ text: 'Rendben', role: 'cancel' }, { text: 'Mégis megpróbálom', role: 'ok' }],
    });
    await a.present();
    return (await a.onDidDismiss()).role === 'ok';
  }
  async navAllapot() {
    this.m2mFut = 'allapot';
    try {
      const r: any = await this.ny.get(`/bevallas/${this.bev.id}/nav-allapot`);
      const v = r.valasz || {};
      this.jelez(`NAV (${r.kornyezet}) ${r.muvelet}: ${v.bizonylatStatusz || v.resultCode || 'ismeretlen'}`, 'success');
    } catch (e: any) { this.jelez(e?.error?.error || 'Nem sikerült lekérdezni.', 'danger'); }
    finally { this.m2mFut = null; }
  }

  navNaplo: any[] | null = null;
  async navNaploNyit() {
    try { this.navNaplo = ((await this.ny.get<any>(`/bevallas/${this.bev.id}/m2m-naplo`))?.sorok) || []; }
    catch (e: any) { this.jelez(e?.error?.error || 'Nem sikerült betölteni a NAV-naplót.', 'danger'); }
  }
  navNaploFajl(n: any, mit: 'xml' | 'valasz' | 'hibalista') {
    this.ny.fajlMegnyit(`/api/nav-nyomtatvany/bevallas/${this.bev.id}/m2m-naplo/${n.id}/${mit}`, `NAV ${n.muvelet} – ${mit}`);
  }

  private async jelez(m: string, color: string) { (await this.toast.create({ message: m, duration: 2400, color, position: 'top' })).present(); }
}
